import { useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function on<T extends Window | Document | HTMLElement | EventTarget>(
    obj: T | null,
    ...args: Parameters<T['addEventListener']> | [string, Function | null, ...any]
): void {
    if (obj && obj.addEventListener) {
        obj.addEventListener(...(args as Parameters<HTMLElement['addEventListener']>));
    }
}

export function off<T extends Window | Document | HTMLElement | EventTarget>(
    obj: T | null,
    ...args: Parameters<T['removeEventListener']> | [string, Function | null, ...any]
): void {
    if (obj && obj.removeEventListener) {
        obj.removeEventListener(...(args as Parameters<HTMLElement['removeEventListener']>));
    }
}
const useLifecycles = (mount: any, unmount?: any) => {
    useEffect(() => {
        if (mount) {
            mount();
        }
        return () => {
            if (unmount) {
                unmount();
            }
        };
    }, []);
};

const bareHash = (value: string) => value.replace(/^#/, '');

export const useHash = () => {
    const [hash, setHash] = useState(() => window.location.hash);
    const { key: locationKey } = useLocation();

    const onHashChange = useCallback(() => {
        setHash(window.location.hash);
    }, []);

    useLifecycles(
        () => {
            on(window, 'hashchange', onHashChange);
        },
        () => {
            off(window, 'hashchange', onHashChange);
        },
    );

    // A react-router navigation changes the hash through history.pushState, which fires no
    // `hashchange` — without this the hook keeps serving the hash of the page we navigated away
    // from and the view stays on the old tab. Compared bare so a `setHash('x')` write (which stores
    // the value unprefixed) is not rewritten to '#x' behind its caller's back, and returning the
    // current value makes the no-op case a React bail-out rather than a re-render loop.
    useEffect(() => {
        setHash((current) => (bareHash(current) === bareHash(window.location.hash) ? current : window.location.hash));
    }, [locationKey]);

    // `replace` is opt-in: a view that only STATES what it is already showing (a resolved sub-tab
    // written back into the address bar) must not leave a history entry, or Back walks that view's
    // internal states instead of leaving the page. Either way the hook's own state is updated —
    // a bare history.replaceState leaves this state stale and re-fires whatever effect wrote it.
    const _setHash = useCallback(
        (newHash: string, options?: { replace?: boolean }) => {
            if (newHash !== hash) {
                const write = options?.replace ? history.replaceState.bind(history) : history.pushState.bind(history);
                if ('' == newHash) {
                    write('', document.title, window.location.pathname + window.location.search);
                } else {
                    write(null, '', newHash > '' ? '#' + newHash : '');
                }
                setHash(newHash);
            }
        },
        [hash],
    );

    return [hash, _setHash] as const;
};
